import { Component, inject, signal } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { AsyncPipe } from '@angular/common';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { map, shareReplay } from 'rxjs/operators';
import { RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { MAIN_MENU_CONFIG_TOKEN } from './main-menu-config.token';
import type { MainMenuItem, MenuItem } from './main-menu-config.type';
import { MenuComponent } from './menu.component';
import { FilterMenuItemsByRolePipe } from './filter-menuItems-by-role.pipe';
import { DESKTOP_BP } from './desktop-breakpoint';
import { TOP_RIGHT_MENU_CONFIG_TOKEN } from './top-right-menu-config.token';
import { CustomMatSvgIconDirective } from '../common/custom-mat-svg-icon/custom-mat-svg-icon.directive';
import { ResolveTranslateAbleTextPipe } from '@pct/ng-i18n';
import { AUTH_SERVICE_INJECTION_TOKEN } from '../auth/auth-service-injection-token';
import type { AdminUserRole } from '../../backend/main';
import { RouteTitleWatcherService } from '@pct/ng-core-router';
import { environment } from '../../environment';

@Component({
  selector: 'app-layout',
  templateUrl: './app-layout.component.html',
  imports: [
    MatToolbarModule,
    MatButtonModule,
    MatSidenavModule,
    MatListModule,
    MatIconModule,
    AsyncPipe,
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    MenuComponent,
    CustomMatSvgIconDirective,
    ResolveTranslateAbleTextPipe,
    FilterMenuItemsByRolePipe,
  ],
  styleUrl: './app-layout.component.scss',
  host: {
    class: 'block h-full max-md:bg-[#EBEBEB]',
    style: '--mat-list-active-indicator-shape: 0;',
  },
})
export class AppLayoutComponent {
  protected readonly environment = environment;
  readonly #breakpointObserver = inject(BreakpointObserver);
  readonly title = inject(RouteTitleWatcherService).title;
  protected readonly mainMenuConfig = inject(MAIN_MENU_CONFIG_TOKEN);
  protected readonly appTopRightMenuConfig = inject(TOP_RIGHT_MENU_CONFIG_TOKEN);
  private readonly authService = inject(AUTH_SERVICE_INJECTION_TOKEN);
  protected readonly me = this.authService.me;
  protected readonly selectedMainMenuItem = signal<MainMenuItem<AdminUserRole>>(
    this.mainMenuConfig.items[0],
  );
  protected readonly appVersion = environment.version;
  protected readonly topRightMenuConfig: MenuItem<AdminUserRole>[] = [
    ...this.appTopRightMenuConfig,
    {
      name: 'log-out',
      label: 'PROFILE_MENU.LOG_OUT',
      click: this.logout.bind(this),
      icon: 'pct-mdi-logout',
      role: ['SystemAdmin', 'Admin'],
    },
    {
      name: 'app-version',
      label: {
        text: 'PROFILE_MENU.APP_VERSION',
        translate: true,
        translateParams: { version: this.appVersion },
      },
      icon: 'pct-mdi-information-outline',
      role: ['SystemAdmin', 'Admin'],
    },
  ];

  protected readonly isHandset$ = this.#breakpointObserver.observe(DESKTOP_BP).pipe(
    map(result => result.matches),
    shareReplay(),
  );

  protected onIsActiveChangeMainMenuItem(
    menuItem: MainMenuItem<AdminUserRole>,
    $event: boolean,
  ): void {
    if ($event === true) {
      this.selectedMainMenuItem.set(menuItem);
    }
  }

  logout(): void {
    this.authService.logoutWithConfirmDialog();
  }
}
